// src/lib/lieferscheine.js
// --- LIEFERSCHEINE AN EINER WARTUNG (Anna-Lena 21.09.2026, Migration 66) -----
//
//   1. Das Buero laedt die Lieferschein-PDF an der Wartung hoch (Tabelle
//      lieferscheine, privater Speicher "lieferscheine").
//   2. Oeffnet der Monteur das Protokoll MIT Netz, legt die App die PDF im Geraet
//      ab (IndexedDB) - so klappt das Unterschreiben auch im Keller ohne Empfang.
//   3. Der Kunde unterschreibt auf dem Handy. pdf-lib stempelt Unterschrift, Name
//      und Datum unten rechts auf die LETZTE Seite. Das Ergebnis liegt zuerst im
//      Geraet und geht raus, sobald Netz da ist (wie die Fotos).
//   4. Das Original bleibt unveraendert; das unterschriebene PDF liegt daneben.
//
// Eigene IndexedDB ("etb_lieferscheine_v1"): Store "original" (id -> PDF) und
// Store "signiert" (id -> fertiges PDF, wartet auf Upload).

import { supabase, withTimeout } from './supabase';
import { istNetzproblem } from './warteschlange';

const DB_NAME = 'etb_lieferscheine_v1';
const ORIGINAL = 'original';
const SIGNIERT = 'signiert';
const BUCKET = 'lieferscheine';
const LISTE = 'etb-lieferscheine-';     // + protokollId: gemerkte Liste fuer ohne Netz
const EREIGNIS = 'etb-lieferscheine-geaendert';

// --- Geraetespeicher -----------------------------------------------------------
let verbindung = null;
function db() {
  if (verbindung) return verbindung;
  verbindung = new Promise((ok, fehler) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const d = req.result;
      if (!d.objectStoreNames.contains(ORIGINAL)) d.createObjectStore(ORIGINAL, { keyPath: 'id' });
      if (!d.objectStoreNames.contains(SIGNIERT)) d.createObjectStore(SIGNIERT, { keyPath: 'id' });
    };
    req.onsuccess = () => {
      const d = req.result;
      d.onclose = () => { verbindung = null; };
      d.onversionchange = () => { try { d.close(); } catch { /* egal */ } verbindung = null; };
      ok(d);
    };
    req.onerror = () => fehler(req.error);
  });
  verbindung.catch(() => { verbindung = null; });
  return verbindung;
}
// Eine Transaktion; alle Anfragen synchron in "arbeit" (kein await darin).
async function tx(store, modus, arbeit, nochmal = true) {
  try {
    const d = await Promise.race([db(), new Promise((_, f) => setTimeout(() => f(new Error('Gerätespeicher antwortet nicht')), 8000))]);
    return await new Promise((ok, fehler) => {
      const t = d.transaction(store, modus);
      const s = t.objectStore(store);
      let ergebnis;
      arbeit(s, (r) => { ergebnis = r; });
      t.oncomplete = () => ok(ergebnis);
      t.onerror = () => fehler(t.error);
      t.onabort = () => fehler(t.error || new Error('Abgebrochen'));
    });
  } catch (e) {
    verbindung = null;
    if (nochmal) return tx(store, modus, arbeit, false);
    throw e;
  }
}
const holen = (store, id) => tx(store, 'readonly', (s, setze) => { const r = s.get(id); r.onsuccess = () => setze(r.result || null); });
const ablegen = (store, eintrag) => tx(store, 'readwrite', (s) => { s.put(eintrag); });
const entfernen = (store, id) => tx(store, 'readwrite', (s) => { s.delete(id); });
const alle = (store) => tx(store, 'readonly', (s, setze) => { const r = s.getAll(); r.onsuccess = () => setze(r.result || []); });

function melden(protokollId) {
  try { window.dispatchEvent(new CustomEvent(EREIGNIS, { detail: { protokollId } })); } catch { /* egal */ }
}
export function beiLieferscheinAenderung(cb) {
  const h = (e) => cb(e.detail || {});
  window.addEventListener(EREIGNIS, h);
  return () => window.removeEventListener(EREIGNIS, h);
}

// --- Liste --------------------------------------------------------------------
function listeMerken(protokollId, liste) {
  try { localStorage.setItem(LISTE + protokollId, JSON.stringify(liste)); } catch { /* nur Bequemlichkeit */ }
}
export function gemerkteListe(protokollId) {
  try { return JSON.parse(localStorage.getItem(LISTE + protokollId) || 'null'); } catch { return null; }
}

// Liste vom Server; ohne Netz die gemerkte. Wartende Unterschriften obendrauf.
export async function lieferscheineLaden(protokollId) {
  let liste = null;
  let ohneNetz = false;
  try {
    const { data, error } = await withTimeout(supabase.from('lieferscheine')
      .select('*').eq('protokoll_id', protokollId).order('erstellt_am'), 20000, 'Lieferscheine laden');
    if (error) throw error;
    liste = data || [];
    listeMerken(protokollId, liste);
  } catch (e) {
    // Vor Migration 66 gibt es die Tabelle nicht - dann einfach keine Lieferscheine.
    if (/does not exist|schema cache/i.test(e?.message || '')) return { liste: [], ohneNetz: false };
    liste = gemerkteListe(protokollId) || [];
    ohneNetz = true;
  }
  const wartend = await alle(SIGNIERT).catch(() => []);
  const liste2 = liste.map(l => {
    const w = wartend.find(x => x.id === l.id);
    return w && !l.signiert_pfad
      ? { ...l, unterschrift_name: w.name, unterschrieben_am: w.am, wartetAufNetz: true }
      : l;
  });
  return { liste: liste2, ohneNetz };
}

// Originale ins Geraet holen, damit der Monteur spaeter ohne Netz unterschreiben
// kann. Laeuft im Hintergrund beim Oeffnen des Protokolls.
export async function originaleVorladen(liste) {
  for (const l of liste || []) {
    if (l.signiert_pfad) continue;
    try {
      if (await holen(ORIGINAL, l.id)) continue;
      const { data, error } = await withTimeout(supabase.storage.from(BUCKET).download(l.datei_pfad), 120000, 'Lieferschein laden');
      if (error) throw error;
      await ablegen(ORIGINAL, { id: l.id, blob: data, zeit: Date.now() });
    } catch { /* ohne Netz oder Fehler - beim naechsten Oeffnen wieder */ }
  }
}

async function originalHolen(l) {
  const g = await holen(ORIGINAL, l.id).catch(() => null);
  if (g?.blob) return g.blob;
  const { data, error } = await withTimeout(supabase.storage.from(BUCKET).download(l.datei_pfad), 120000, 'Lieferschein laden');
  if (error) throw error;
  await ablegen(ORIGINAL, { id: l.id, blob: data, zeit: Date.now() }).catch(() => {});
  return data;
}

// Die PDF-Datei zum Anzeigen in der App: das unterschriebene (auch das noch
// wartende), sonst das Original. welches: 'aktuell' | 'original'.
export async function lieferscheinBlob(l, welches = 'aktuell') {
  if (welches === 'original') return originalHolen(l);
  const w = await holen(SIGNIERT, l.id).catch(() => null);
  if (w?.blob) return w.blob;
  if (l.signiert_pfad) {
    const { data, error } = await withTimeout(supabase.storage.from(BUCKET).download(l.signiert_pfad), 120000, 'Lieferschein laden');
    if (error) throw error;
    return data;
  }
  return originalHolen(l);
}

// PDF ansehen: das unterschriebene (auch das noch wartende), sonst das Original.
export async function lieferscheinOeffnen(l, fenster) {
  const w = await holen(SIGNIERT, l.id).catch(() => null);
  let blob = w?.blob || null;
  if (!blob && l.signiert_pfad) {
    const { data, error } = await withTimeout(supabase.storage.from(BUCKET).createSignedUrl(l.signiert_pfad, 300), 20000, 'Link');
    if (error) throw error;
    if (fenster) fenster.location.href = data.signedUrl; else window.open(data.signedUrl, '_blank');
    return;
  }
  if (!blob) blob = await originalHolen(l);
  const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
  if (fenster) fenster.location.href = url; else window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 5 * 60 * 1000);
}

// --- Buero: hochladen und loeschen ------------------------------------------
export async function lieferscheinHochladen({ protokollId, stationId, datei }) {
  if (!datei) return;
  if (!/pdf$/i.test(datei.type || '') && !/\.pdf$/i.test(datei.name || '')) {
    throw new Error('Bitte eine PDF-Datei wählen.');
  }
  const sauber = (datei.name || 'lieferschein.pdf').replace(/[^\w.-]+/g, '_');
  const pfad = protokollId + '/' + Date.now() + '-' + sauber;
  const up = await withTimeout(supabase.storage.from(BUCKET).upload(pfad, datei,
    { upsert: false, contentType: 'application/pdf' }), 120000, 'Hochladen');
  if (up.error) throw up.error;
  const ins = await withTimeout(supabase.from('lieferscheine').insert({
    protokoll_id: protokollId, station_id: stationId || null, datei_pfad: pfad,
    datei_name: datei.name || sauber, titel: (datei.name || '').replace(/\.pdf$/i, '') || 'Lieferschein',
  }), 20000, 'Speichern');
  if (ins.error) throw ins.error;
  melden(protokollId);
}

export async function lieferscheinLoeschen(l) {
  const del = await withTimeout(supabase.from('lieferscheine').delete().eq('id', l.id), 20000, 'Löschen');
  if (del.error) throw del.error;
  const pfade = [l.datei_pfad, l.signiert_pfad].filter(Boolean);
  if (pfade.length) await supabase.storage.from(BUCKET).remove(pfade).catch(() => {});
  await entfernen(ORIGINAL, l.id).catch(() => {});
  melden(l.protokoll_id);
}

// --- Unterschreiben ----------------------------------------------------------
// Weisse Flaeche der Unterschrift durchsichtig machen: gestempelt wird nur die
// Tinte - sonst deckte ein weisses Rechteck den Text des Lieferscheins ab.
async function tinteAlsPng(dataUrl) {
  const img = await new Promise((ok, fehler) => {
    const i = new Image(); i.onload = () => ok(i); i.onerror = fehler; i.src = dataUrl;
  });
  const c = document.createElement('canvas');
  c.width = img.width; c.height = img.height;
  const ctx = c.getContext('2d');
  ctx.drawImage(img, 0, 0);
  const bild = ctx.getImageData(0, 0, c.width, c.height);
  const p = bild.data;
  for (let i = 0; i < p.length; i += 4) {
    if (p[i] > 225 && p[i + 1] > 225 && p[i + 2] > 225) p[i + 3] = 0;
  }
  ctx.putImageData(bild, 0, 0);
  const blob = await new Promise(ok => c.toBlob(ok, 'image/png'));
  c.width = 0; c.height = 0;
  return new Uint8Array(await blob.arrayBuffer());
}

const zwei = (n) => String(n).padStart(2, '0');
// Die eingebaute PDF-Schrift kennt nur westeuropaeische Zeichen. Fremde
// Buchstaben (z. B. c mit Akzent bei kroatischen Namen) werden vereinfacht,
// sonst bricht das Stempeln ab.
const lateinisch = (s) => Array.from(String(s || '')).map(z => (z.charCodeAt(0) <= 255 ? z
  : (z.normalize('NFD').replace(/[̀-ͯ]/g, '') || '?'))).join('').replace(/[^\x20-\xff]/g, '?');
function zeitText(d) {
  return zwei(d.getDate()) + '.' + zwei(d.getMonth() + 1) + '.' + d.getFullYear() + ', ' + zwei(d.getHours()) + ':' + zwei(d.getMinutes()) + ' Uhr';
}

// Stempelt Unterschrift + Name + Zeitpunkt auf die letzte Seite. Geht ohne Netz,
// wenn das Original schon im Geraet liegt.
// position: { seite, x, y } in PDF-Koordinaten - y ist die Hoehe der
// Unterschriftslinie (von der App gefunden oder vom Monteur angetippt). Ohne
// position wie frueher unten rechts auf der letzten Seite.
export async function lieferscheinUnterschreiben(l, { name, unterschrift, userId, position }) {
  if (!name || !String(name).trim()) throw new Error('Bitte den Namen des Kunden eintragen.');
  if (!unterschrift) throw new Error('Bitte unterschreiben lassen.');
  let original;
  try {
    original = await originalHolen(l);
  } catch (e) {
    throw new Error(istNetzproblem(e) || !navigator.onLine
      ? 'Dieser Lieferschein ist noch nicht auf dem Gerät. Bitte einmal mit Netz das Protokoll öffnen.'
      : (e.message || String(e)));
  }
  // Erst hier laden: pdf-lib ist gross und wird nur zum Unterschreiben gebraucht.
  const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib');
  const doc = await PDFDocument.load(await original.arrayBuffer(), { ignoreEncryption: true });
  const seiten = doc.getPages();
  const seite = position && seiten[position.seite - 1] ? seiten[position.seite - 1] : seiten[seiten.length - 1];
  const { width } = seite.getSize();
  const schrift = await doc.embedFont(StandardFonts.Helvetica);
  const fett = await doc.embedFont(StandardFonts.HelveticaBold);
  const bild = await doc.embedPng(await tinteAlsPng(unterschrift));
  const jetzt = new Date();

  if (position) {
    // Auf der Linie: Unterschrift sitzt mit der Unterkante auf der Linie, Name und
    // Zeitpunkt klein darueber. Nie ueber den rechten Rand hinaus.
    const b = 150;
    const h = Math.min(44, (bild.height / bild.width) * b);
    const bw = Math.min(b, h * bild.width / bild.height);
    const px = Math.max(8, Math.min(position.x, width - bw - 8));
    const py = position.y + 1;
    seite.drawImage(bild, { x: px, y: py, width: bw, height: h });
    seite.drawText(lateinisch(String(name).trim()) + '  ·  ' + zeitText(jetzt),
      { x: px, y: py + h + 2, size: 7, font: schrift, color: rgb(0.3, 0.3, 0.35) });
  } else {
    // Feld unten rechts: 220 x 88 pt, 60 pt ueber dem Seitenrand - darunter liegt bei
    // Lieferscheinen meist die Fusszeile (Bank, Firmenangaben).
    const breite = 220;
    const x = width - breite - 28;
    const y = 60;
    const hoehe = Math.min(52, (bild.height / bild.width) * breite);
    seite.drawImage(bild, { x, y: y + 30, width: Math.min(breite, hoehe * bild.width / bild.height), height: hoehe });
    seite.drawLine({ start: { x, y: y + 28 }, end: { x: x + breite, y: y + 28 }, thickness: 0.6, color: rgb(0.3, 0.3, 0.35) });
    seite.drawText('Empfang bestätigt: ' + lateinisch(String(name).trim()), { x, y: y + 16, size: 8.5, font: fett, color: rgb(0.04, 0.09, 0.16) });
    seite.drawText(zeitText(jetzt), { x, y: y + 5, size: 7.5, font: schrift, color: rgb(0.3, 0.3, 0.35) });
  }

  const bytes = await doc.save();
  const blob = new Blob([bytes], { type: 'application/pdf' });
  await ablegen(SIGNIERT, { id: l.id, protokollId: l.protokoll_id, blob, name: String(name).trim(),
    am: jetzt.toISOString(), userId: userId || null, fehler: null });
  melden(l.protokoll_id);
  unterschriebeneSenden().catch(() => {});
}

// --- Senden der unterschriebenen PDFs --------------------------------------------
let laufend = null;
export function unterschriebeneSenden() {
  if (laufend) return laufend;
  laufend = (async () => {
    if (!navigator.onLine) return;
    const liste = await alle(SIGNIERT).catch(() => []);
    for (const w of liste) {
      const pfad = w.protokollId + '/' + w.id + '-unterschrieben.pdf';
      try {
        const up = await withTimeout(supabase.storage.from(BUCKET).upload(pfad, w.blob,
          { upsert: false, contentType: 'application/pdf' }), 120000, 'Lieferschein senden');
        if (up.error && !/already exists|duplicate|409/i.test(String(up.error.message || '') + ' ' + String(up.error.statusCode || ''))) throw up.error;
        const { data, error } = await withTimeout(supabase.from('lieferscheine').update({
          signiert_pfad: pfad, unterschrift_name: w.name, unterschrieben_am: w.am, unterschrieben_von: w.userId,
        }).eq('id', w.id).select('id'), 30000, 'Lieferschein eintragen');
        if (error) throw error;
        if (!data || data.length === 0) throw new Error('Lieferschein nicht gefunden (inzwischen gelöscht?).');
        await entfernen(SIGNIERT, w.id);
        await entfernen(ORIGINAL, w.id).catch(() => {});
        melden(w.protokollId);
      } catch (e) {
        if (istNetzproblem(e) || /jwt|token/i.test(String(e?.message || ''))) break;
        // Echte Ablehnung: im Geraet behalten und rot anzeigen - nie still weg.
        await ablegen(SIGNIERT, { ...w, fehler: e.message || String(e) }).catch(() => {});
        melden(w.protokollId);
      }
    }
  })().finally(() => { laufend = null; });
  return laufend;
}

let automatik = false;
export function starteLieferscheinAutomatik() {
  if (automatik) return;
  automatik = true;
  window.addEventListener('online', () => { unterschriebeneSenden().catch(() => {}); });
  setInterval(() => { unterschriebeneSenden().catch(() => {}); }, 60000);
  unterschriebeneSenden().catch(() => {});
}

// Fehler einer wartenden Unterschrift (fuer die rote Anzeige).
export async function wartendeFehler(id) {
  const w = await holen(SIGNIERT, id).catch(() => null);
  return w?.fehler || null;
}
